<template>
    <div class="profile-page-wrapper">
        <!-- Colonne de gauche - Sidebar -->
        <aside class="profile-sidebar">
            <div class="sidebar-user-info">
                <div class="sidebar-avatar" v-if="!showProfilePhoto">
                    {{ userInitials }}
                </div>
                <img
                    v-else
                    :src="profileImageUrl"
                    alt="Photo"
                    class="sidebar-avatar-img"
                    @error="onProfileImageError"
                />
                <h3 class="sidebar-name">{{ userName }}</h3>
                <p class="sidebar-email">{{ userEmail }}</p>
            </div>

            <nav class="sidebar-menu">
                <router-link :to="{ name: 'nouveau_profil' }" class="menu-item" active-class="active" exact-active-class="active">
                    <i class="fa fa-user"></i>
                    <span>Profil</span>
                </router-link>
                <router-link :to="{ name: 'conversations-list' }" class="menu-item" active-class="active">
                    <i class="fa fa-envelope"></i>
                    <span>Messages</span>
                </router-link>
                <router-link :to="{ name: 'all-requests' }" class="menu-item" active-class="active">
                    <i class="fa fa-calendar"></i>
                    <span>Mes Réservations</span>
                </router-link>
                <router-link :to="{ name: 'my-trips' }" class="menu-item" active-class="active">
                    <i class="fa fa-car"></i>
                    <span>Mes Trajets</span>
                </router-link>
                <router-link :to="{ name: 'all-trips' }" class="menu-item" active-class="active">
                    <i class="fa fa-star"></i>
                    <span>Mes Demandes</span>
                </router-link>
            </nav>

            <div class="sidebar-footer">
                <a href="#" class="menu-item logout" @click.prevent="handleLogout">
                    <i class="fa fa-sign-out"></i>
                    <span>Déconnexion</span>
                </a>
            </div>
        </aside>

        <!-- Colonne de droite - Contenu principal -->
        <main class="profile-main">
            <!-- En-tête -->
            <header class="profile-header">
                <h1>Votre Profil</h1>
                <p class="header-subtitle">Mettez à jour vos informations personnelles et vos préférences.</p>
            </header>

            <!-- Formulaire -->
            <div class="profile-card">
                <!-- Photo de profil -->
                <div class="profile-photo-section">
                    <div class="photo-wrapper">
                        <div class="profile-avatar" v-if="!showProfilePhoto" @click="triggerPhotoUpload">
                            {{ userInitials }}
                        </div>
                        <img
                            v-else
                            :src="profileImageUrl"
                            alt="Photo de profil"
                            class="profile-image"
                            @click="triggerPhotoUpload"
                            @error="onProfileImageError"
                        />
                    </div>
                    <div class="photo-actions">
                        <p class="photo-label">Photo de profil</p>
                        <button type="button" class="btn-change-photo" @click="triggerPhotoUpload">
                            Cliquez sur l'avatar pour changer votre photo.
                        </button>
                        <input ref="photoInput" type="file" accept="image/*" style="display: none" @change="handlePhotoChange" />
                        <p v-if="!showProfilePhoto" class="avatar-default-message">
                            Sans photo de profil, vos initiales (nom et prénom) s’affichent.
                        </p>
                    </div>
                </div>

                <!-- Formulaire -->
                <form @submit.prevent="updateProfile">
                    <!-- Nom complet -->
                    <div class="form-group">
                        <label for="name">Nom complet</label>
                        <input id="name" v-model="formData.name" type="text" class="form-input" placeholder="Votre nom" maxlength="50" :class="{ 'input-error': errors.name }" />
                        <span v-if="errors.name" class="error-text">{{ errors.name }}</span>
                    </div>

                    <!-- Adresse e-mail -->
                    <div class="form-group">
                        <label for="email">Adresse e-mail</label>
                        <input id="email" :value="userEmail" type="email" class="form-input" disabled />
                    </div>

                    <!-- Numéro de téléphone -->
                    <div class="form-group">
                        <label for="phone">Numéro de téléphone</label>
                        <p v-if="!phoneVerified" class="error-text-inline">
                            Votre numéro de téléphone n'est pas vérifié.
                        </p>
                        <div class="phone-input-wrapper">
                            <input id="phone" v-model="formData.mobile_phone" type="tel" class="form-input phone-input" placeholder="+33" :class="{ 'input-error': errors.mobile_phone }" />
                            <button type="button" class="btn-verify" @click="verifyPhone" :disabled="!formData.mobile_phone || phoneVerified">
                                {{ phoneVerified ? 'Vérifié' : 'Vérifier' }}
                            </button>
                        </div>
                        <span v-if="errors.mobile_phone" class="error-text">{{ errors.mobile_phone }}</span>
                    </div>

                    <!-- Bio -->
                    <div class="form-group">
                        <div class="bio-label-row">
                            <label for="bio">Bio</label>
                            <button type="button" class="btn-generate-ai" @click="generateBio" :disabled="loadingAI">
                                <i class="fa fa-magic"></i>
                                {{ loadingAI ? 'Génération...' : 'Générer avec l\'IA' }}
                            </button>
                        </div>
                        <textarea id="bio" v-model="formData.description" class="form-textarea" placeholder="Parlez-nous un peu de vous..." rows="4" maxlength="2000" :class="{ 'input-error': errors.description }"></textarea>
                        <span v-if="errors.description" class="error-text">{{ errors.description }}</span>
                    </div>

                    <!-- Bouton de mise à jour -->
                    <div class="form-actions">
                        <button type="submit" class="btn-submit" :disabled="loading">
                            <span v-if="!loading">Mettre à jour le profil</span>
                            <span v-else><i class="fa fa-spinner fa-spin"></i> Mise à jour...</span>
                        </button>
                    </div>
                </form>
            </div>

            <!-- Zone de danger -->
            <div class="danger-zone">
                <h2>Zone de danger</h2>
                <p>Les actions dans cette zone sont dangereuses et irréversibles.</p>
                <button type="button" class="btn-danger" @click="showDeleteModal = true">
                    Supprimer mon compte
                </button>
            </div>
        </main>

        <!-- Modal de suppression -->
        <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h3>Confirmer la suppression</h3>
                    <button class="modal-close" @click="showDeleteModal = false">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <p>Êtes-vous sûr de vouloir supprimer votre compte ?</p>
                    <p class="warning-text">Cette action est irréversible.</p>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel" @click="showDeleteModal = false">Annuler</button>
                    <button class="btn-confirm-delete" @click="deleteAccount" :disabled="loadingDelete">
                        {{ loadingDelete ? 'Suppression...' : 'Supprimer mon compte' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Modal de vérification SMS -->
        <div v-if="showSmsModal" class="modal-overlay" @click="showSmsModal = false">
            <div class="modal-content modal-sms" @click.stop>
                <div class="modal-header">
                    <h3>Vérification du téléphone</h3>
                    <button class="modal-close" @click="showSmsModal = false">
                        <i class="fa fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <SmsIdentityValidation @verified="onPhoneVerified" @close="showSmsModal = false" />
                </div>
            </div>
        </div>

        <!-- Footer séparé -->
        <footer class="profile-footer">
            <div class="footer-container">
                <div class="footer-left">
                    <div class="footer-logo-section">
                        <img src="https://i.imgur.com/z198fac.png" alt="MyEcoCar Logo" class="footer-logo" />
                        <span class="footer-brand-name">MyEcoCar</span>
                    </div>
                    <div class="footer-social-links">
                        <a href="#" class="social-link" title="Facebook"><i class="fa fa-facebook-f"></i></a>
                        <a href="#" class="social-link" title="Twitter"><i class="fa fa-twitter"></i></a>
                        <a href="#" class="social-link" title="Instagram"><i class="fa fa-instagram"></i></a>
                        <a href="#" class="social-link" title="LinkedIn"><i class="fa fa-linkedin"></i></a>
                    </div>
                </div>
                <div class="footer-center">
                    <div class="footer-column">
                        <h4 class="footer-column-title">Infos</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'cgu' }">CGU</router-link></li>
                            <li><router-link :to="{ name: 'faq' }">FAQ</router-link></li>
                            <li><router-link :to="{ name: 'contact' }">Contact</router-link></li>
                        </ul>
                    </div>
                    <div class="footer-column">
                        <h4 class="footer-column-title">Découvrir</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'about' }">Qui sommes-nous ?</router-link></li>
                            <li><router-link :to="{ name: 'conduct-charter' }">Charte de bonne conduite</router-link></li>
                            <li><router-link :to="{ name: 'map' }">Carte Interactive</router-link></li>
                        </ul>
                    </div>
                    <div class="footer-column">
                        <h4 class="footer-column-title">Plus</h4>
                        <ul class="footer-links">
                            <li><router-link :to="{ name: 'concept' }">Notre Concept</router-link></li>
                            <li><router-link :to="{ name: 'cookies' }">Utilisation des cookies</router-link></li>
                            <li><router-link :to="{ name: 'sitemap' }">Plan du site</router-link></li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="footer-bottom">
                <p class="footer-copyright">&copy; {{ currentYear }} MyEcoCar. Tous les droits réservés.</p>
            </div>
        </footer>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SmsIdentityValidation from '../sections/SmsIdentityValidation.vue';
import dialogs from '../../services/dialogs.js';
import { UserApi } from '../../services/api';
import { getRoute } from '../../services/utility';

export default {
    name: 'SimpleProfile',
    components: { SmsIdentityValidation },
    data() {
        return {
            formData: { name: '', mobile_phone: '', description: '' },
            errors: {},
            loading: false,
            loadingAI: false,
            loadingDelete: false,
            showDeleteModal: false,
            showSmsModal: false,
            userApi: null,
            profileImageFailed: false,
            /** Force le navigateur à recharger l’image après upload (même nom de fichier). */
            profilePhotoCacheKey: 0
        };
    },
    computed: {
        ...mapGetters({ user: 'auth/user' }),
        currentYear() { return new Date().getFullYear(); },
        userName() { return this.user && this.user.name ? this.user.name : ''; },
        userEmail() { return this.user && this.user.email ? this.user.email : ''; },
        /** Photo personnalisée uploadée (champ image renseigné côté API). */
        hasCustomProfileImage() {
            const img = this.user && this.user.image != null ? String(this.user.image).trim() : '';
            return img.length > 0;
        },
        profileImageUrl() {
            if (!this.hasCustomProfileImage) return '';
            return getRoute(this.user.image, '/image/profile/');
        },
        /** Affiche la photo seulement si URL valide et pas d’erreur de chargement → sinon initiales. */
        showProfilePhoto() {
            return this.hasCustomProfileImage && this.profileImageUrl && !this.profileImageFailed;
        },
        phoneVerified() { return this.user && this.user.phone_verified_at; },
        /** Nom utilisé pour les initiales : saisie en cours du formulaire, sinon profil enregistré. */
        nameForInitials() {
            const fromForm = this.formData && typeof this.formData.name === 'string' ? this.formData.name.trim() : '';
            if (fromForm.length) return fromForm;
            return this.user && this.user.name ? String(this.user.name).trim() : '';
        },
        /**
         * Initiales prénom + nom : 1re lettre du 1er mot + 1re lettre du dernier mot (ex. « Jean Dupont » → JD).
         * Un seul mot : deux premières lettres.
         */
        userInitials() {
            const raw = this.nameForInitials.replace(/\s+/g, ' ').trim();
            if (!raw.length) return '?';
            const parts = raw.split(' ').filter(Boolean);
            if (parts.length === 1) {
                const w = parts[0];
                return w.substring(0, Math.min(2, w.length)).toUpperCase();
            }
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
    },
    mounted() {
        this.userApi = new UserApi();
        this.initializeForm();
    },
    watch: {
        user: { handler() { this.initializeForm(); }, immediate: true, deep: true },
        'user.image'() {
            this.profileImageFailed = false;
        }
    },
    methods: {
        ...mapActions({ setUser: 'auth/setUser', logout: 'auth/logout' }),
        /** Réponse API Fractal `{ data: { ...user } }` ou objet user direct. */
        userPayloadFromApi(res) {
            if (!res || typeof res !== 'object') return null;
            if (res.data && typeof res.data === 'object' && res.data.id != null) return res.data;
            if (res.id != null) return res;
            return null;
        },
        initializeForm() {
            if (!this.user) return;
            this.formData = {
                name: this.user.name || '',
                mobile_phone: this.user.mobile_phone || '',
                description: this.user.description || ''
            };
        },
        validateForm() {
            this.errors = {};
            if (!this.formData.name || !this.formData.name.trim()) {
                this.errors.name = 'Le nom est requis';
                return false;
            }
            if (this.formData.mobile_phone && !/^[+]?[0-9\s\-()]+$/.test(this.formData.mobile_phone)) {
                this.errors.mobile_phone = 'Format de téléphone invalide';
                return false;
            }
            return true;
        },
        async updateProfile() {
            if (!this.validateForm()) {
                dialogs.message('Veuillez corriger les erreurs', { duration: 5, estado: 'error' });
                return;
            }
            this.loading = true;
            try {
                const formData = new FormData();
                formData.append('name', this.formData.name);
                formData.append('mobile_phone', this.formData.mobile_phone);
                formData.append('description', this.formData.description);
                const response = await this.userApi.update(formData);
                const userPayload = this.userPayloadFromApi(response);
                if (userPayload) {
                    this.setUser(userPayload);
                    dialogs.message('Profil mis à jour avec succès', { duration: 3, estado: 'success' });
                }
            } catch (error) {
                dialogs.message('Erreur lors de la mise à jour', { duration: 5, estado: 'error' });
            } finally {
                this.loading = false;
            }
        },
        onProfileImageError() {
            this.profileImageFailed = true;
        },
        triggerPhotoUpload() { this.$refs.photoInput.click(); },
        async handlePhotoChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            if (!file.type.startsWith('image/')) {
                dialogs.message('Veuillez sélectionner une image', { duration: 3, estado: 'error' });
                return;
            }
            if (file.size > 4 * 1024 * 1024) {
                dialogs.message('L\'image ne doit pas dépasser 4 MB', { duration: 3, estado: 'error' });
                return;
            }
            this.loading = true;
            try {
                const formData = new FormData();
                formData.append('image', file);
                const response = await this.userApi.update(formData);
                const userPayload = this.userPayloadFromApi(response);
                if (userPayload) {
                    this.setUser(userPayload);
                    this.profilePhotoCacheKey = Date.now();
                    this.profileImageFailed = false;
                    dialogs.message('Photo mise à jour avec succès', { duration: 3, estado: 'success' });
                }
            } catch (error) {
                dialogs.message('Erreur lors de l\'upload', { duration: 5, estado: 'error' });
            } finally {
                this.loading = false;
                if (this.$refs.photoInput) {
                    this.$refs.photoInput.value = '';
                }
            }
        },
        async generateBio() {
            this.loadingAI = true;
            try {
                const response = await this.userApi.generateBio();
                if (response && response.bio) {
                    this.formData.description = response.bio;
                    dialogs.message('Bio générée ! N\'oubliez pas de sauvegarder.', { duration: 4, estado: 'success' });
                }
            } catch (error) {
                dialogs.message('Erreur lors de la génération', { duration: 5, estado: 'error' });
            } finally {
                this.loadingAI = false;
            }
        },
        verifyPhone() { this.showSmsModal = true; },
        onPhoneVerified() {
            this.showSmsModal = false;
            dialogs.message('Téléphone vérifié', { duration: 3, estado: 'success' });
        },
        async deleteAccount() {
            this.loadingDelete = true;
            try {
                await this.userApi.deleteAccount();
                dialogs.message('Votre compte a été supprimé', { duration: 3, estado: 'success' });
                setTimeout(() => { this.logout(); this.$router.push({ name: 'trips' }); }, 2000);
            } catch (error) {
                dialogs.message('Erreur lors de la suppression', { duration: 5, estado: 'error' });
                this.loadingDelete = false;
            }
        },
        handleLogout() { this.logout(); }
    },
};
</script>

<style scoped>
.profile-page-wrapper {
    display: flex;
    flex-wrap: wrap;
    min-height: 100vh;
    background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
}

.profile-sidebar {
    width: 280px;
    flex-shrink: 0;
    background: white;
    border-right: 1px solid #e0e0e0;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

.sidebar-user-info {
    padding: 24px 16px;
    text-align: center;
    border-bottom: 1px solid #e0e0e0;
}

.sidebar-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 600;
    color: #757575;
    margin-bottom: 12px;
}

.sidebar-avatar-img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 12px;
}

.sidebar-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 4px 0;
}

.sidebar-email {
    font-size: 12px;
    color: #666;
    margin: 0;
}

.sidebar-menu {
    flex: 1;
    padding: 8px 0;
}

.menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    color: #666;
    text-decoration: none;
    font-size: 14px;
    transition: background 0.2s, color 0.2s;
}

.menu-item i {
    font-size: 16px;
    width: 20px;
    text-align: center;
}

.menu-item:hover {
    background: #f5f5f5;
    color: #4caf50;
}

.menu-item.active {
    background: #4caf50;
    color: white;
    font-weight: 500;
}

.sidebar-footer {
    border-top: 1px solid #e0e0e0;
    padding: 8px 0;
}

.menu-item.logout {
    color: #f44336;
}

.menu-item.logout:hover {
    background: #ffebee;
    color: #d32f2f;
}

.profile-main {
    flex: 1;
    min-width: 0;
    padding: 0 32px 32px;
}

.profile-header {
    background: linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%);
    padding: 24px;
    border-radius: 12px 12px 0 0;
}

.profile-header h1 {
    font-size: 24px;
    font-weight: 700;
    color: #2e7d32;
    margin: 0 0 6px 0;
}

.header-subtitle {
    font-size: 13px;
    color: #558b2f;
    margin: 0;
}

.profile-card {
    background: white;
    border-radius: 0 0 12px 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 24px;
}

.profile-photo-section {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    padding-bottom: 24px;
    border-bottom: 1px solid #e0e0e0;
}

.photo-wrapper { flex-shrink: 0; }

.profile-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    font-weight: 600;
    color: #757575;
    cursor: pointer;
}

.profile-image {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    cursor: pointer;
}

.photo-actions { flex: 1; }

.photo-label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin: 0 0 4px 0;
}

.btn-change-photo {
    background: none;
    border: none;
    color: #4caf50;
    font-size: 13px;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
}

.avatar-default-message {
    color: #666;
    font-size: 11px;
    margin: 4px 0 0 0;
}

.form-group { margin-bottom: 20px; }

.form-group label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 6px;
}

.form-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    background: #e8f5e9;
}

.form-input:focus {
    outline: none;
    border-color: #4caf50;
    background: white;
}

.form-input:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
}

.form-input.input-error { border-color: #f44336; }

.error-text {
    display: block;
    color: #f44336;
    font-size: 11px;
    margin-top: 4px;
}

.error-text-inline {
    color: #f44336;
    font-size: 11px;
    margin: 0 0 6px 0;
    display: block;
}

.phone-input-wrapper {
    display: flex;
    gap: 10px;
}

.phone-input { flex: 1; }

.btn-verify {
    padding: 10px 20px;
    background: #4caf50;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
}

.btn-verify:hover:not(:disabled) { background: #388e3c; }

.btn-verify:disabled { background: #9e9e9e; cursor: not-allowed; }

.bio-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.bio-label-row label { margin: 0; }

.form-textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    background: #e8f5e9;
}

.form-textarea:focus {
    outline: none;
    border-color: #4caf50;
    background: white;
}

.btn-generate-ai {
    margin: 0;
    padding: 6px 12px;
    background: linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.btn-generate-ai:hover:not(:disabled) { transform: translateY(-1px); }

.btn-generate-ai:disabled { opacity: 0.6; cursor: not-allowed; }

.form-actions { margin-top: 24px; }

.btn-submit {
    width: 100%;
    padding: 12px 20px;
    background: #4caf50;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.btn-submit:hover:not(:disabled) { background: #388e3c; }

.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

.danger-zone {
    background: white;
    border: 2px solid #f44336;
    border-radius: 12px;
    padding: 20px 24px;
}

.danger-zone h2 {
    font-size: 18px;
    font-weight: 700;
    color: #f44336;
    margin: 0 0 8px 0;
}

.danger-zone p {
    color: #666;
    margin: 0 0 16px 0;
    font-size: 13px;
}

.btn-danger {
    padding: 10px 20px;
    background: #f44336;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.btn-danger:hover { background: #d32f2f; }

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-sms { max-width: 600px; }

.modal-header {
    padding: 20px 20px 14px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    color: #333;
}

.modal-close {
    background: none;
    border: none;
    font-size: 22px;
    color: #999;
    cursor: pointer;
    padding: 0;
}

.modal-body { padding: 20px; }

.modal-body p { margin: 0 0 10px 0; color: #666; font-size: 13px; }

.warning-text { color: #f44336 !important; font-weight: 500; }

.modal-footer {
    padding: 14px 20px 20px;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}

.btn-cancel {
    padding: 8px 16px;
    background: #e0e0e0;
    color: #333;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
}

.btn-confirm-delete {
    padding: 8px 16px;
    background: #f44336;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
}

.btn-confirm-delete:hover:not(:disabled) { background: #d32f2f; }

.btn-confirm-delete:disabled { opacity: 0.6; cursor: not-allowed; }

.profile-footer {
    width: 100%;
    background: linear-gradient(135deg, #1f3f2e 0%, #2d5016 100%);
    color: #ffffff;
    padding: 24px 16px 16px;
}

.footer-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 24px;
}

.footer-left {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.footer-logo-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.footer-logo {
    width: 44px;
    height: 44px;
    object-fit: contain;
}

.footer-brand-name {
    font-family: 'Poppins', sans-serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: #aef7b9;
}

.footer-social-links {
    display: flex;
    gap: 8px;
}

.social-link {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aef7b9;
    text-decoration: none;
}

.social-link:hover {
    background: rgba(174, 247, 185, 0.2);
    color: #ffffff;
}

.footer-center {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.footer-column { display: flex; flex-direction: column; gap: 8px; }

.footer-column-title {
    font-family: 'Poppins', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    color: #aef7b9;
    margin: 0;
}

.footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.footer-links a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 0.85rem;
}

.footer-links a:hover { color: #aef7b9; }

.footer-bottom {
    text-align: center;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(174, 247, 185, 0.2);
}

.footer-copyright {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
}

@media (max-width: 900px) {
    .profile-page-wrapper { flex-direction: column; }
    .profile-sidebar { width: 100%; border-right: none; border-bottom: 1px solid #e0e0e0; }
    .sidebar-menu { display: flex; overflow-x: auto; padding: 0; }
    .menu-item { flex-direction: column; gap: 4px; padding: 10px 14px; font-size: 11px; }
    .menu-item span { display: block; }
    .profile-main { padding: 16px; }
    .profile-card { padding: 16px; }
    .profile-photo-section { flex-direction: column; align-items: flex-start; }
    .phone-input-wrapper { flex-direction: column; }
    .bio-label-row { flex-direction: column; align-items: flex-start; gap: 6px; }
    .footer-container { grid-template-columns: 1fr; }
    .footer-center { grid-template-columns: 1fr 1fr; }
}
</style>

<style>
.app-container.nouveau_profil .footer-component { display: none !important; }
</style>